import { workspaceAccessConfig } from '@/config/workspace-access';
import { z } from 'zod';

const template = (placeholder: string) =>
  z
    .string()
    .min(1)
    .refine(
      (value) => value.split(placeholder).length === 2,
      `Expected exactly one ${placeholder} placeholder.`
    );

export const imageUploadTooltipCopySchema = z
  .object({
    maximumImages: template('{maximumImages}'),
    freeMaxSize: template('{freeMaxSize}'),
    paidMaxSize: template('{paidMaxSize}'),
    sharedLimit: template('{size}'),
  })
  .strict();
export type ImageUploadTooltipCopy = z.infer<
  typeof imageUploadTooltipCopySchema
>;

function formatUploadSize(bytes: number) {
  const sizeInMb = bytes / 1024 / 1024;
  if (!Number.isFinite(sizeInMb) || sizeInMb <= 0) {
    throw new Error(`Upload tooltip size must be positive: ${bytes}.`);
  }
  return `${Number.isInteger(sizeInMb) ? sizeInMb : sizeInMb.toFixed(1)} MB`;
}

export function buildAdminV2ImageUploadTooltip(
  params: { maxImages?: number; freeMaxBytes: number; paidMaxBytes: number },
  copy: ImageUploadTooltipCopy
): string {
  const freeMaxSize = formatUploadSize(params.freeMaxBytes);
  const paidMaxSize = formatUploadSize(params.paidMaxBytes);
  const maximumImages =
    typeof params.maxImages === 'number' && params.maxImages > 1
      ? String(params.maxImages)
      : '';
  return [
    ...(maximumImages
      ? [copy.maximumImages.replace('{maximumImages}', maximumImages)]
      : []),
    ...(workspaceAccessConfig.paidEntitlementsEnabled
      ? [
          copy.freeMaxSize.replace('{freeMaxSize}', freeMaxSize),
          copy.paidMaxSize.replace('{paidMaxSize}', paidMaxSize),
        ]
      : [copy.sharedLimit.replace('{size}', paidMaxSize)]),
  ].join('\n');
}
